import { formatJsonRpcError, formatJsonRpcResult } from '@json-rpc-tools/utils';
import { SESSION_EVENTS } from '../Constants';
const STORAGE_KEY = '@reown/web-wallet/session-users';
// Manages WalletConnect sessions, user identity mapping, and session persistence
export class SessionController {
    walletKit;
    appKit;
    sessionUsers;
    constructor(walletKit, appKit) {
        this.walletKit = walletKit;
        this.appKit = appKit;
        this.sessionUsers = this.loadSessionUsers();
    }
    loadSessionUsers() {
        if (typeof window === 'undefined')
            return new Map();
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (!stored)
                return new Map();
            const parsed = JSON.parse(stored);
            return new Map(Object.entries(parsed));
        }
        catch (err) {
            console.error('[SessionController] LoadSessionUsers. Failed to load session users from storage:', err);
            return new Map();
        }
    }
    saveSessionUsers() {
        if (typeof window === 'undefined')
            return;
        try {
            const existingData = window.localStorage.getItem(STORAGE_KEY);
            const existingUsers = existingData ? JSON.parse(existingData) : {};
            const newUsers = Object.fromEntries(this.sessionUsers);
            const mergedUsers = {};
            Object.entries(newUsers).forEach(([key, newUser]) => {
                mergedUsers[key] = { ...newUser };
            });
            // Then preserve loginProvider from existing users if available
            // This is necessary because AppKit sometimes switches auth provider
            // from social login to email which makes it impossible for us to reconnect
            Object.entries(existingUsers).forEach(([key, existingUser]) => {
                if (mergedUsers[key] && existingUser.loginProvider && !mergedUsers[key].loginProvider) {
                    mergedUsers[key].loginProvider = existingUser.loginProvider;
                }
                else if (!mergedUsers[key]) {
                    mergedUsers[key] = existingUser;
                }
            });
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedUsers));
        }
        catch (err) {
            console.error('[SessionController] SaveSessionUsers. Failed to save session users to storage:', err);
        }
    }
    async getUser() {
        if (!this.appKit)
            throw new Error('AppKit not initialized');
        // We use getAccount to get embedded wallet info which is the same for all namespaces
        const account = this.appKit.getAccount();
        const user = account?.embeddedWalletInfo?.user;
        const loginProvider = account?.embeddedWalletInfo?.authProvider;
        if (!user) {
            throw new Error('User info not found');
        }
        if (!user.email && !user.username) {
            throw new Error('User is missing email and username');
        }
        return {
            email: user.email,
            username: user.username,
            loginProvider: loginProvider
        };
    }
    async getPendingRequests() {
        if (!this.walletKit)
            throw new Error('WalletKit not initialized');
        const requests = this.walletKit.getPendingSessionRequests();
        return requests
            .filter(request => this.sessionUsers.has(request.topic))
            .map(request => ({
            ...request,
            user: this.sessionUsers.get(request.topic)
        }));
    }
    async respondToRequest(request, { result, error }) {
        if (error) {
            await this.walletKit.respondSessionRequest({
                topic: request.topic,
                response: formatJsonRpcError(request.id, error instanceof Error ? error.message : 'User rejected')
            });
        }
        else {
            await this.walletKit.respondSessionRequest({
                topic: request.topic,
                response: formatJsonRpcResult(request.id, result)
            });
        }
    }
    subscribeToSessionRequests(callback) {
        if (!this.walletKit)
            throw new Error('WalletKit not initialized');
        const handler = (request) => {
            const user = this.sessionUsers.get(request.topic);
            if (user) {
                callback({ ...request, user });
            }
        };
        this.walletKit.on('session_request', handler);
        return () => {
            this.walletKit.off('session_request', handler);
        };
    }
    getUserForSession(topic) {
        return this.sessionUsers.get(topic);
    }
    saveUserForSession(topic, user) {
        this.sessionUsers.set(topic, user);
        this.saveSessionUsers();
    }
    async updateUserEmail(oldEmail, newEmail, targetSessionTopic) {
        if (!this.walletKit)
            throw new Error('WalletKit not initialized');
        const allSessions = this.walletKit.getActiveSessions();
        if (!allSessions)
            return;
        let activeChain = this.appKit?.getChainId();
        if (typeof activeChain === 'number') {
            activeChain = `eip155:${activeChain}`;
        }
        const sessionsToDisconnect = [];
        let targetSessionFound = false;
        for (const [topic, session] of Object.entries(allSessions)) {
            const properties = session.sessionProperties || {};
            if (properties.provider !== 'email' || properties.email !== oldEmail)
                continue;
            if (topic !== targetSessionTopic) {
                sessionsToDisconnect.push(topic);
                continue;
            }
            targetSessionFound = true;
            try {
                await this.walletKit.emitSessionEvent({
                    topic: targetSessionTopic,
                    event: {
                        name: SESSION_EVENTS.UPDATE_EMAIL,
                        data: { email: newEmail }
                    },
                    chainId: activeChain
                });
                if (this.sessionUsers.has(targetSessionTopic)) {
                    const user = this.sessionUsers.get(targetSessionTopic);
                    this.sessionUsers.set(targetSessionTopic, {
                        ...user,
                        email: newEmail
                    });
                    this.saveSessionUsers();
                }
            }
            catch (error) {
                console.error(`[SessionController] Failed to emit event or update user data for ${targetSessionTopic}:`, error);
            }
        }
        if (!targetSessionFound) {
            console.warn(`[SessionController] Target session ${targetSessionTopic} not found among active sessions with email ${oldEmail}.`);
        }
        if (sessionsToDisconnect.length === 0)
            return;
        for (const topic of sessionsToDisconnect) {
            try {
                await this.walletKit.disconnectSession({
                    topic,
                    reason: { code: 4001, message: 'User changed email address' }
                });
                this.sessionUsers.delete(topic);
            }
            catch (error) {
                console.error(`[SessionController] Failed to disconnect session ${topic}:`, error);
                if (this.sessionUsers.has(topic)) {
                    this.sessionUsers.delete(topic);
                }
            }
        }
        this.saveSessionUsers();
    }
}
//# sourceMappingURL=SessionController.js.map